import * as Constants from './Constants';
import endImageLoadPerfDataPoint from './endImageLoadPerfDataPoint';
import getUrlResponseData, { ResponseData } from './getUrlResponseData';
import loadFromUrl from './loadFromUrl';
import logDiagnosticData from './logDiagnosticData';
import logImageLoadPerfData from './logImageLoadPerfData';
import logImageLoadError from './logImageLoadError';
import safeGetAttribute from './safeGetAttribute';
import startImageLoadPerfDataPoint from './startImageLoadPerfDataPoint';
import { isFeatureEnabled } from 'owa-feature-flags';
import { INLINEIMAGE_ATTRIBUTE_ORIGINALSRC } from 'owa-inline-image-consts';

const ATTRIBUTE_IMAGEERROR = 'data-imageerror';
const ATTRIBUTE_OUTLOOKTRACE = 'data-outlook-trace';

const ERROR_CID_TILDEWRD = 'CidTildeWrd';
const ERROR_ATTNOTFOUND = 'AttNotFound';

const CID_TILDEWRD_REGEX = new RegExp('^cid:\\s?~WRD', 'i');

const CID_REGEX = new RegExp('^cid:', 'i');

// Regex used to extract found and total count from data-outlook-trace
const FOUND_COUNT_REGEX = new RegExp('f:([0-9]+)', 'i');
const TOTAL_COUNT_REGEX = new RegExp('t:([0-9]+)', 'i');

export default function loadImage(img: HTMLImageElement, context: string) {
    const imageError = safeGetAttribute(img, ATTRIBUTE_IMAGEERROR);
    const imageType = safeGetAttribute(img, Constants.ATTRIBUTE_IMAGETYPE);
    const originalSrc = safeGetAttribute(img, INLINEIMAGE_ATTRIBUTE_ORIGINALSRC);
    if (imageError && imageError.length > 0) {
        // Bug 30333: Log AttNotFound from cid:~WRD000 differently to get break down
        // https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/30333
        let adjustedImageError = imageError;
        if (originalSrc && CID_TILDEWRD_REGEX.test(originalSrc)) {
            adjustedImageError = ERROR_CID_TILDEWRD;
        } else if (imageError == ERROR_ATTNOTFOUND) {
            const outlookTrace = safeGetAttribute(img, ATTRIBUTE_OUTLOOKTRACE);
            const breakdown = getOutlookTraceBreakdown(outlookTrace);
            if (breakdown) {
                adjustedImageError = `${ERROR_ATTNOTFOUND}_${breakdown}`;
            }
        }

        // If data-imageerror is set, log an error perf data point
        logImageLoadPerfData(
            imageType /* type */,
            '' /* howLoad */,
            adjustedImageError /* error */,
            context /* context */
        );

        // Also log an error usage data point
        logImageLoadError(
            imageType /* type */,
            '' /* howLoad */,
            adjustedImageError /* error */,
            '' /* statusCode */,
            context /* context */,
            '' /* loaderCode */,
            originalSrc /* originalSrc*/,
            '' /* contentType */
        );

        // loadImage is called from InlineImageLoader as a last resort to load image
        // when we're here, it indicates it is an error image and src is 1*1 place holder
        // we should clean the onload attribute and src to show to the user that there is
        // an image here although it is broken one
        img.onload = null;

        // Bug 51037: Remove src completely for inline image when it is not found
        // https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/51037
        // Restoring src to originalSrc which in most cases is cid:xxxx will cause browser
        // to show a mixed content warning. To avoid the warning, just remove the src entirely
        // which will have same effect of showing a broken image
        img.removeAttribute('src');

        // Bug 27136: Enable error reporting in SDF when there is error for loading inline image (for errors like AttNotFound, NotImage, 404 etc.)
        // This change is temporary. We would like to get some real repro for the error to troubleshoot the issue
        if (isFeatureEnabled('mc-imageDiagnostics')) {
            if (imageError == ERROR_ATTNOTFOUND || imageError.indexOf('NotImage') == 0) {
                logDiagnosticData(img, imageError);
            }
        }
    } else if (originalSrc && !CID_REGEX.test(originalSrc)) {
        const loadDataPoint = startImageLoadPerfDataPoint();
        loadFromUrl(
            img,
            originalSrc,
            (imageElement: HTMLImageElement) => {
                endImageLoadPerfDataPoint(loadDataPoint, imageType, '', '' /* error */, context);
            },
            (imageElement: HTMLImageElement) => {
                endImageLoadPerfDataPoint(
                    loadDataPoint,
                    imageType,
                    '' /* howLoad */,
                    Constants.ERROR_ONERROR,
                    context
                );

                // In addition to the perf data point, try to get response code and log an error data point
                getUrlResponseData(originalSrc).then((responseData: ResponseData) => {
                    const { status, responseCode, contentType } = responseData;
                    const error = status || responseCode.toString();
                    logImageLoadError(
                        imageType /* type */,
                        '' /* howLoad */,
                        error /* error */,
                        responseCode.toString() /* statusCode*/,
                        context /* context */,
                        '' /* loaderCode*/,
                        originalSrc /* originalSrc*/,
                        contentType /* contentType */
                    );
                });
            }
        );
    }
}

/**
 * Get outlook trace break down
 */
function getOutlookTraceBreakdown(outlookTrace: string): string {
    // Expected format of the trace:
    // F:x|T:x, F means found and it indicates number of times the attachment is seen when it is processed in HtmlUpdateBodyCallback. T means total and it indicates number of times it passes through callback
    // - When missing: it indicates the content hasn't ever been processed by the callback - NoTrace
    // - When F == 0: the attachment is not seen first time or to say never seen the content is processed in callback - PreEWS
    // - When 0 < F < T: found < total, means the attachment is ever seen in the callback but was not found later - MidEWS
    // - When T == F: the attachment is seen every time the content passes through callback. A late reply outside EWS dropped the attachment - PostEWS
    let breakdown = '';
    if (outlookTrace) {
        const foundMatches: string[] = FOUND_COUNT_REGEX.exec(outlookTrace);
        const totalMatches: string[] = TOTAL_COUNT_REGEX.exec(outlookTrace);
        if (foundMatches && foundMatches.length == 2 && totalMatches && totalMatches.length == 2) {
            const foundCount = parseInt(foundMatches[1]);
            const totalCount = parseInt(totalMatches[1]);
            if (!isNaN(foundCount) && !isNaN(totalCount)) {
                if (foundCount == 0) {
                    // This indicates dropping of inline attachments happened before EWS
                    breakdown = 'PreEWS';
                } else if (foundCount > 0) {
                    if (foundCount < totalCount) {
                        // This indicates dropping of inline attachments happened somewhere in-between
                        breakdown = 'MidEWS';
                    } else if (foundCount == totalCount) {
                        // This indicates dropping of inline attachments happened after EWS
                        breakdown = 'PostEWS';
                    }
                }
            }
        }
    } else {
        // This indicates EWS does not participate in procesing of the message
        breakdown = 'NoTrace';
    }

    return breakdown;
}
